import { Button, Grid, Tabs } from 'antd';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
// eslint-disable-next-line max-len
import { type InsuranceProductDetailsWithPermissionsDataFragment } from '../../../../api/fragments/InsuranceProductDetailsWithPermissionsData';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import AssignVehiclesForm from '../../FinanceProductDetailsPage/FinanceProductForm/AssignVehiclesForm';
import MainDetails from './MainDetails';
import { type InsuranceProductFormValues } from './shared';

enum Tab {
    MainDetails = 'MainDetails',
    Vehicles = 'Vehicles',
}

type InsuranceProductFormProps = {
    insuranceProduct?: InsuranceProductDetailsWithPermissionsDataFragment;
    deleteInsuranceProduct: () => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    updated?: string;
    offset?: string;
    permissions: Partial<Pick<PermissionValues, 'hasUpdatePermission' | 'hasDeletePermission'>>;
};
const InsuranceProductForm = ({
    deleteInsuranceProduct,
    handleSubmit,
    offset,
    updated,
    permissions,
    insuranceProduct,
}: InsuranceProductFormProps) => {
    const { initialValues } = useFormikContext<InsuranceProductFormValues>();
    const { t } = useTranslation('insuranceProductDetails');
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();

    const [activeTab, setActiveTab] = useState<string>(Tab.MainDetails);

    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const content = useMemo(() => {
        switch (activeTab) {
            case Tab.MainDetails:
                return (
                    <MainDetails
                        disabled={!hasUpdatePermission}
                        insuranceProduct={insuranceProduct}
                        offset={offset}
                        updated={updated}
                    />
                );
            case Tab.Vehicles:
                return <AssignVehiclesForm disabled={!hasUpdatePermission} />;
            default:
                return null;
        }
    }, [activeTab, hasUpdatePermission, insuranceProduct, offset, updated]);

    return (
        <ConsolePageWithHeader
            extra={
                hasDeletePermission && (
                    <Button onClick={deleteInsuranceProduct} danger>
                        {t('insuranceProductDetails:actions.delete')}
                    </Button>
                )
            }
            footer={[
                hasUpdatePermission && (
                    <Button key="submit" form="insuranceProductForm" htmlType="submit" type="primary">
                        {t('insuranceProductDetails:actions.update')}
                    </Button>
                ),
            ]}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <Tabs.TabPane key={Tab.MainDetails} tab={t('insuranceProductDetails:mainTitles.mainDetails')} />
                        <Tabs.TabPane key={Tab.Vehicles} tab={t('insuranceProductDetails:mainTitles.assignVehicles')} />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/insuranceProducts')}
            title={
                screens.md
                    ? t('insuranceProductDetails:title', { name: initialValues.displayName })
                    : initialValues.displayName
            }
        >
            <Form id="insuranceProductForm" name="insuranceProductForm" onSubmitCapture={handleSubmit}>
                <FormAutoTouch />
                {content}
            </Form>
        </ConsolePageWithHeader>
    );
};

export default InsuranceProductForm;
